import React from 'react'
import { Link, usePage } from '@inertiajs/react'
import {
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Breadcrumbs,
} from '@mui/material'
import {
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
} from '@mui/icons-material'
import ManagementLayout from '@/components/layouts/ManagementLayout'
import EmptyState from '@/components/shared/EmptyState'
import { PageHeader, FlexRow, CodeBlock } from '@/components/styled'
import type { VirtualBucket, Backend, Account } from '@/types'

interface Props {
  backend: Backend
  virtualBuckets: VirtualBucket[]
}

export default function VirtualBucketsIndex({ backend, virtualBuckets }: Props) {
  const { currentAccount, isAccountAdmin } = usePage<{
    currentAccount: Account
    isAccountAdmin: boolean
  }>().props

  return (
    <ManagementLayout>
      <PageHeader
        title="Virtual Buckets"
        description={`Virtual buckets for ${backend.name}.`}
        breadcrumb={
          <Breadcrumbs>
            <Link href={`/accounts/${currentAccount.slug}/backends`}>Backends</Link>
            <Link href={`/accounts/${currentAccount.slug}/backends/${backend.slug}`}>{backend.name}</Link>
            <Typography color="text.primary">Virtual Buckets</Typography>
          </Breadcrumbs>
        }
        action={
          isAccountAdmin && (
            <Button
              component={Link}
              href={`/accounts/${currentAccount.slug}/backends/${backend.slug}/virtual_buckets/new`}
              variant="contained"
              startIcon={<AddIcon />}
            >
              Add Virtual Bucket
            </Button>
          )
        }
      />

      {virtualBuckets.length > 0 ? (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Base Path</TableCell>
                <TableCell>Public Access</TableCell>
                <TableCell>Credentials</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {virtualBuckets.map((vb) => (
                <TableRow key={vb.id} hover>
                  <TableCell>
                    <Link href={`/virtual_buckets/${vb.id}`}>
                      {vb.name}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {vb.base_path ? (
                      <CodeBlock>{vb.base_path}</CodeBlock>
                    ) : (
                      <Typography variant="body2" color="text.secondary">Root</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {(vb.public_read || vb.public_list) ? (
                      <FlexRow sx={{ gap: 0.5 }}>
                        {vb.public_read && <Chip label="Read" size="small" color="success" />}
                        {vb.public_list && <Chip label="List" size="small" color="info" />}
                      </FlexRow>
                    ) : (
                      <Chip label="Private" size="small" />
                    )}
                  </TableCell>
                  <TableCell>{vb.credentialsCount ?? 0}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      component={Link}
                      href={`/virtual_buckets/${vb.id}`}
                      size="small"
                    >
                      <VisibilityIcon />
                    </IconButton>
                    {isAccountAdmin && (
                      <IconButton
                        component={Link}
                        href={`/virtual_buckets/${vb.id}/edit`}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <EmptyState
          icon="folder"
          title="No virtual buckets"
          description="Create your first virtual bucket."
          actionPath={isAccountAdmin ? `/accounts/${currentAccount.slug}/backends/${backend.slug}/virtual_buckets/new` : undefined}
          actionText={isAccountAdmin ? 'Add Virtual Bucket' : undefined}
        />
      )}
    </ManagementLayout>
  )
}
